import { useTranslation } from 'react-i18next';

import '@/components/tavolo/tavolo.css';
import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';
import type { Board, Row } from '@/lib/qcp/protocol';
import { FILE, conSegno } from '@/lib/qcp/resoconto';
import type { ChiaveVoce, LatoResoconto, Resoconto } from '@/lib/qcp/resoconto';
import { CpEtichettaPlancia } from './CpTavolo';
import type { ModoTavolo } from './CpTavolo';
import { OfcBoard } from './OfcBoard';
import type { InfoFila } from './OfcBoard';

/**
 * IL RESOCONTO DELLA MANO (fase 2; tavole 4, 5 e 8 della scheda `2026-10-17-tavolo-premium-ofc.md`): le due plance finali con, per ogni fila, l'esito (+1/−1/0) e le royalty
 * PAGATE; il badge «Scoop +3» di chi ha vinto tutte e tre, il badge «Foul» di chi lo fa (con le sue file sbiadite e nessun esito per fila: il server non lo manda); in fondo la
 * somma — righe vinte, scoop, le tue royalty, quelle dell'avversario — e il totale della mano.
 * ⛔ LEGGE e non ricalcola (`lib/qcp/resoconto.ts`): ogni cifra e un campo di `breakdown` o il netto del server; se le voci non tornano al totale la somma non si mostra.
 * Senza `breakdown` (forfeit, un server piu vecchio) o senza le plance rivelate si mostra quello che c'e: le plance senza cifre, o il solo totale.
 * «Prossima mano» chiude il resoconto e manda il SEED della mano dopo (il server aspetta il seed di tutti e due i posti prima di distribuire: l'hook `useQcpMatch` lo tiene finche
 * il resoconto e aperto); se nessuno tocca, il seed parte da solo allo scadere del conto, che il tasto mostra («Prossima mano · 12»). A partita finita il tasto dice «Vedi il risultato»
 * e porta alla schermata del risultato: nessun seed e nessun conto.
 */
const infoDi = (lato: LatoResoconto | null): InfoFila | undefined =>
  lato ? (Object.fromEntries(FILE.map((f: Row) => [f, { esito: lato.esiti[f], royalty: lato.royalty[f] }])) as InfoFila) : undefined;

/** Nel palco la striscia delle voci e il tasto «Prossima mano» stanno in fondo: dal bordo alto della striscia (le plance finiscono a ~262) al bordo basso del palco meno 14 px. */
const PALCO_SOMMA_TOP = 282;
const PALCO_SOMMA_ALTEZZA = 94;

const ETICHETTA_VOCE: Record<ChiaveVoce, string> = {
  righeVinte: 'resoconto.righeVinte',
  scoop: 'resoconto.scoop',
  scoopAvversario: 'resoconto.scoopAvversario',
  foulAvversario: 'resoconto.foulAvversario',
  tuoFoul: 'resoconto.tuoFoul',
  foulEntrambi: 'resoconto.foulEntrambi',
  tueRoyalty: 'resoconto.tueRoyalty',
  royaltyAvversario: 'resoconto.royaltyAvversario',
};

function Badge({ tipo, valore }: { tipo: 'foul' | 'scoop'; valore?: string }) {
  const { t } = useTranslation('chinesepoker');
  return tipo === 'foul' ? (
    <span data-badge="foul" className="rounded-full bg-rose-400/20 px-3 py-0.5 text-[12px] font-extrabold uppercase tracking-wider text-rose-300 shadow-[inset_0_0_0_1.5px_rgba(251,113,133,.45)]">
      {t('resoconto.foul')}
    </span>
  ) : (
    <span data-badge="scoop" className="rounded-full bg-[#d4af6a]/20 px-3 py-0.5 text-[12px] font-extrabold uppercase tracking-wider text-[#ecd39a] shadow-[inset_0_0_0_1.5px_rgba(212,175,106,.55)]">
      ✦ {t('resoconto.scoop')} {valore}
    </span>
  );
}

export function CpResoconto({
  modo,
  r,
  quattroColori,
  onProssima,
  ultima = false,
  secondi = null,
}: {
  modo: ModoTavolo;
  r: Resoconto;
  quattroColori: boolean;
  /** Senza, il tasto «Prossima mano» non c'e` (il tutorial va avanti con la sua scheda guida). */
  onProssima?: () => void;
  /** e l'ultima mano della partita: il tasto porta al risultato */
  ultima?: boolean;
  /** i secondi che mancano al seed automatico (`null` = nessun conto) */
  secondi?: number | null;
}) {
  const { t } = useTranslation('chinesepoker');
  const palco = modo === 'palco';
  // nelle tavole 4, 5 e 8 le due plance hanno carte della stessa misura (36×50): con le 48 della plancia di gioco la colonna di destra (esito e royalty) non ci sta in 390 px
  const tagliaMia = 'avv';
  const badgeDi = (l: LatoResoconto | null) => (l?.foul ? <Badge tipo="foul" /> : l?.scoop ? <Badge tipo="scoop" valore="+3" /> : undefined);
  const plance = r.plance as { io: Board; avversario: Board } | null;
  const colore = r.netto > 0 ? 'text-emerald-300' : r.netto < 0 ? 'text-rose-300' : 'text-fg-muted';

  const plancia = (chi: 'io' | 'avversario') => {
    const lato = chi === 'io' ? r.io : r.avversario;
    return (
      <div data-plancia-resoconto={chi} className="flex flex-col gap-1">
        <CpEtichettaPlancia nome={chi === 'io' ? t('resoconto.tu') : t('match.opponent')} badge={badgeDi(lato)} />
        <OfcBoard board={plance![chi]} taglia={chi === 'io' ? tagliaMia : 'avv'} quattroColori={quattroColori} info={infoDi(lato)} sbiadita={lato?.foul === true} filoSulleLibere={false} />
      </div>
    );
  };

  const totale = (
    <span data-totale-mano className={cn('text-[24px] font-extrabold tabular-nums', colore)}>
      {conSegno(r.netto)}
    </span>
  );

  // In piedi la somma e le sole VOCI (il totale sta nel piede incollato, con «Prossima mano»); nel palco una striscia con le voci e il totale.
  const somma = !palco ? (
    r.voci ? (
      <div data-somma-resoconto className="tav-vetro tav-rilievo tav-riga-entra flex flex-col rounded-2xl px-4 py-2 tabular-nums" style={{ animationDelay: '120ms' }}>
        {r.voci.map((v) => (
          <div key={v.chiave} data-voce={v.chiave} className={cn('flex items-baseline justify-between py-0.5 text-[16px]', v.chiave.startsWith('royaltyAvversario') || v.chiave === 'scoopAvversario' ? 'text-[#8fc2b3]' : 'text-fg')}>
            <span>{v.perFoul ? t(`${ETICHETTA_VOCE[v.chiave]}Foul`) : t(ETICHETTA_VOCE[v.chiave])}</span>
            <span>{conSegno(v.valore)}</span>
          </div>
        ))}
      </div>
    ) : null
  ) : (
    // ⚠️ I-807 («a fine mano il resoconto non si vede correttamente»): la striscia era alta 56 px con `overflow-hidden` e le etichette in UNA riga con l'ellissi. Con padding (16) + etichetta (16) +
    // numero (30) ne servivano 62: l'etichetta usciva SOPRA il bordo e veniva tagliata a meta, e «Le tue royalty», «Royalty dell'avversario» finivano in «…». Ora la striscia e alta quanto serve
    // (`PALCO_SOMMA_ALTEZZA`, sale fino sotto le plance che finiscono a ~262), le etichette vanno a capo (fino a 4 righe, la piu lunga e «Royalty dell'avversario (foul: nessuna)») e nessuna e tagliata.
    <div data-somma-resoconto className="tav-vetro tav-rilievo tav-riga-entra flex min-w-0 flex-1 items-stretch rounded-2xl px-1 py-1.5 tabular-nums">
      {r.voci?.map((v) => (
        <div key={v.chiave} data-voce={v.chiave} className="flex min-w-0 flex-1 flex-col justify-center border-r border-border px-2">
          <span className="break-words text-[10px] font-bold uppercase leading-[12px] tracking-normal text-fg-muted">{v.perFoul ? t(`${ETICHETTA_VOCE[v.chiave]}Foul`) : t(ETICHETTA_VOCE[v.chiave])}</span>
          <span className="text-[22px] font-extrabold leading-7 text-fg">{conSegno(v.valore)}</span>
        </div>
      ))}
      <div className="flex min-w-0 flex-1 flex-col justify-center px-2">
        <span className="break-words text-[10px] font-bold uppercase leading-[12px] tracking-normal text-fg-muted">{t('hand.counter', { n: r.mano })}</span>
        <span className={cn('text-[22px] font-extrabold leading-7', colore)}>{conSegno(r.netto)}</span>
      </div>
    </div>
  );

  const prossima = onProssima === undefined ? null : (
    <Button variant="primary" data-azione="prossima-mano" onClick={onProssima} className={cn('tav-cala !h-12 !px-4 !py-0 text-[16px]', palco ? 'w-[190px] shrink-0' : 'min-w-0 flex-1')}>
      {ultima ? t('resoconto.vediRisultato') : secondi !== null ? t('resoconto.prossimaConto', { s: secondi }) : t('resoconto.prossima')}
    </Button>
  );

  const fantasyland = r.fantasylandPerMe ? (
    <div data-fantasyland-per-me className="rounded-xl bg-[#d4af6a]/15 px-3 py-1.5 text-center text-sm font-semibold text-[#ecd39a]">
      ✦ {t('hand.youInFantasyland')}
    </div>
  ) : null;

  if (palco) {
    return (
      <div data-resoconto-mano data-resoconto-modo="palco" className="absolute inset-0">
        <div className="absolute grid grid-cols-2 gap-5" style={{ left: 24, top: 46, width: 796 }}>
          {plance ? plancia('avversario') : <div />}
          {plance ? plancia('io') : <div />}
        </div>
        {fantasyland && <div className="absolute" style={{ left: 300, top: 8, width: 244 }}>{fantasyland}</div>}
        <div data-piede-palco className="absolute flex items-stretch gap-3" style={{ left: 24, top: PALCO_SOMMA_TOP, width: 796, height: PALCO_SOMMA_ALTEZZA }}>
          {somma}
          <div className="flex shrink-0 items-end">{prossima}</div>
        </div>
      </div>
    );
  }
  // Il piede incollato in fondo (R2 del secondo occhio): il totale della mano e «Prossima mano» si vedono sempre, anche quando le voci scorrono.
  const piede = (
    <div data-piede-resoconto className="sticky bottom-0 z-10 -mx-2 flex items-center gap-3 px-3 pb-1 pt-2" style={{ background: 'linear-gradient(0deg, var(--tav-panno-ombra) 74%, transparent)' }}>
      <div className="flex min-w-0 flex-col leading-none">
        <span className="truncate text-[11px] font-bold uppercase tracking-wider text-fg-muted">{t('hand.counter', { n: r.mano })}</span>
        {totale}
      </div>
      {prossima}
    </div>
  );
  return (
    <div data-resoconto-mano data-resoconto-modo="verticale" className="flex flex-col gap-1">
      {fantasyland}
      {plance && plancia('avversario')}
      {plance && plancia('io')}
      {somma}
      {piede}
    </div>
  );
}
